/**
 * What one panel renders when it has no data — which, as of 2026-08-20, is every
 * data panel on this board.
 *
 * The rule this component exists to enforce: **a refused read never renders as
 * zero, and never as an empty chart.** So when a panel's `access_state` is not
 * `ok`, its charts do not render at all — no strip of em dashes, no flat line
 * along the axis, no empty donut. Zero open incidents and "we could not ask
 * about incidents" are opposite findings, and an empty axis would quietly
 * publish the flattering one (§5 "unconfigured ≠ empty ≠ zero", §8 "never let
 * the shortfall read as a healthy zero").
 *
 * All six `access_state` values get their own heading, tone, icon, owner and
 * remedy list from `ACCESS_COPY` — see `./status.ts`, where the record is typed
 * off the generated union so a new state cannot be forgotten. The three that
 * must not blur: `consent_missing` is "a human must grant a permission",
 * `not_provisioned` is "the product is not deployed here, and a grant would
 * change nothing", and `request_rejected` is "NOVA asked wrongly — raise a
 * ticket against NOVA".
 *
 * Follows `features/dynamics/access-panel.tsx`, widened for the extra thing this
 * backend can say: the exact application permissions Graph refused.
 */

import { Icon } from "@norican/icons";
import { Alert, Badge, Card, DescriptionList, Typography } from "@norican/react";

import { ACCESS_COPY, type DefenderPanelState, formatWindow, NOT_ZERO_NOTE } from "./status";

interface DefenderAccessPanelProps {
  /** What this panel would have shown, e.g. "Open incidents". */
  panel: string;
  /** The payload's state contract. */
  state: DefenderPanelState;
  /**
   * Collapse to a single line, because this exact state has already been
   * explained in full higher up the board.
   *
   * Four panels reading Graph with one credential produce four identical
   * remediation lists when consent is missing, which reads as four separate
   * faults instead of one grant. The first explains it; the rest say "same
   * cause" and name what was not measured.
   */
  repeated?: boolean;
}

export function DefenderAccessPanel({ panel, state, repeated = false }: DefenderAccessPanelProps) {
  const copy = ACCESS_COPY[state.access_state];
  const refused = state.missing_permissions;

  if (repeated) {
    return (
      <Alert
        tone={copy.tone}
        icon={<Icon icon={copy.icon} size="sm" />}
        title={`${panel} — ${copy.badge}`}
      >
        <span className="text-fg-muted">
          Same cause as above{refused.length > 0 ? `: ${refused.join(", ")}` : ""}. Nothing was
          measured for this panel, so nothing is shown — not zero, and not an empty chart.
        </span>
      </Alert>
    );
  }

  const facts = [
    { label: "Panel", value: panel },
    { label: "Window it would cover", value: formatWindow(state.window) },
    {
      label: "Graph credential",
      value: state.configured
        ? "Present — NOVA authenticated as its own application identity"
        : "Absent — nothing was asked of Microsoft Graph at all",
    },
    {
      label: "Permissions this panel needs",
      value: (
        <span className="flex flex-wrap gap-x-2 gap-y-1">
          {state.required_permissions.map((name) => (
            <code key={name} className="text-fg font-mono text-xs break-all">
              {name}
            </code>
          ))}
        </span>
      ),
    },
  ];

  return (
    <Card variant="outline" accent="left" accentTone={copy.tone}>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <Icon icon={copy.icon} size="md" />
            <Typography variant="heading-4" as="h2">
              {copy.title}
            </Typography>
          </div>
          {/* State is never colour-alone (§3.2) — the badge names it in words. */}
          <Badge tone={copy.tone} variant="subtle" size="sm">
            {copy.badge}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          {/* The backend's own one-liner, verbatim. It already names the
              permission, the app registration and the identity — paraphrasing
              would lose the exact strings someone has to act on. */}
          <Alert tone={copy.tone} title={`Microsoft Graph: what happened to "${panel}"`}>
            {state.status_detail}
          </Alert>

          <Typography variant="body-sm" tone="muted">
            {copy.owner}
          </Typography>

          {refused.length > 0 ? (
            <div className="flex flex-col gap-2">
              <Typography variant="label" as="span">
                Grant these — Graph refused them with a 403
              </Typography>
              <ul className="flex flex-col gap-1">
                {refused.map((name) => (
                  <li key={name}>
                    <code className="text-fg bg-bg-muted rounded-sm px-1.5 py-0.5 font-mono text-xs break-all">
                      {name}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            // Never "0 permissions missing": an empty list means nothing has been
            // *proved* missing, which in most of these states means nothing was
            // asked. The state's own note says what the emptiness does and does
            // not mean.
            <Typography variant="body-sm" tone="muted">
              {copy.evidenceNote}
            </Typography>
          )}

          {copy.remedy.length > 0 && (
            <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
              {copy.remedy.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          )}

          <DescriptionList items={facts} columns={2} />

          <Typography variant="body-sm" tone="muted">
            {NOT_ZERO_NOTE}
          </Typography>

          {state.errors.length > 0 && <DefenderErrorsAlert errors={state.errors} />}
        </div>
      </Card.Body>
    </Card>
  );
}

interface DefenderErrorsAlertProps {
  /** The payload's `errors[]` — non-fatal problems hit while collecting. */
  errors: readonly string[];
  /**
   * Heading override. The default suits the partial case (`access_state: "ok"`
   * with one endpoint failed), which is the one that needs the loudest label:
   * real numbers are on screen and they are incomplete.
   */
  title?: string;
}

/**
 * `errors[]` surfaced as an `Alert` beside the figures it undermines (§5), never
 * swallowed — and nothing rendered at all when the array is empty.
 *
 * The backend already folds identical causes into one line and names the read
 * that failed, so the list is rendered as-is rather than deduplicated twice.
 */
export function DefenderErrorsAlert({ errors, title }: DefenderErrorsAlertProps) {
  if (errors.length === 0) {
    return null;
  }
  return (
    <Alert
      tone="warning"
      title={title ?? "This snapshot is incomplete — treat every number below as a floor"}
    >
      <ul className="flex flex-col gap-1">
        {errors.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    </Alert>
  );
}
